import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import {
  ArrowLeft, Download, Trophy, TrendingUp, Target, Brain, Shield,
  MessageCircle, Heart, Sparkles, BarChart3, Users, Building2, ChevronRight,
  CheckCircle, XCircle, Lightbulb, GraduationCap, Activity
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const READINESS_COLORS = {
  Beginner: 'bg-red-500/15 text-red-400',
  Developing: 'bg-amber-500/15 text-amber-400',
  Proficient: 'bg-sky-500/15 text-sky-400',
  Advanced: 'bg-emerald-500/15 text-emerald-400',
  Expert: 'bg-purple-500/15 text-purple-400',
};

export default function SimulationReport() {
  const { simId } = useParams();
  const navigate = useNavigate();
  const { axiosAuth, token } = useAuth();
  const [sim, setSim] = useState(null);
  const [benchmarks, setBenchmarks] = useState(null);
  const [sections, setSections] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const api = axiosAuth();
        const [simRes, benchRes, secRes] = await Promise.all([
          api.get(`/simulations/${simId}`),
          api.get(`/benchmarks/${simId}`).catch(() => null),
          api.get('/report-sections').catch(() => null),
        ]);
        setSim(simRes.data.simulation);
        if (benchRes?.data) setBenchmarks(benchRes.data);
        if (secRes?.data?.sections) setSections(secRes.data.sections);
      } catch { toast.error('Failed to load report'); navigate('/'); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [simId, axiosAuth, navigate]);

  const downloadReport = (type) => {
    fetch(`${API}/simulations/${simId}/report?report_type=${type}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob()).then(blob => { const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `report_${type}.pdf`; a.click(); })
      .catch(() => toast.error('Failed to download'));
  };

  if (loading || !sim) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="heritage-card p-8 text-center"><div className="w-8 h-8 border-2 border-brand border-t-transparent rounded-full animate-spin mx-auto mb-4" /><p className="font-body text-sm">Loading report...</p></div>
    </div>
  );

  const score = sim.score || {};
  const comp = score.competencies || {};
  const tone = score.tone_analysis || {};
  const journey = score.emotion_journey || {};
  const coach = score.ai_coach_summary || {};
  const sec = (key) => sections[key] !== false; // default true if not set

  return (
    <div className="min-h-screen bg-background" data-testid="simulation-report-page">
      <Navbar showBack backTo="/" backLabel="Performance Report" />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight" data-testid="report-title">{sim.scenario_title}</h1>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <Badge className="bg-brand/10 text-brand border-none text-xs">{sim.difficulty}</Badge>
              {sim.variation_title && sim.variation_title !== 'Default' && <Badge className="bg-amber-500/10 text-amber-500 border-none text-xs">{sim.variation_title}</Badge>}
              {coach.readiness_level && <Badge data-testid="readiness-badge" className={`${READINESS_COLORS[coach.readiness_level] || 'bg-muted text-muted-foreground'} border-none text-xs`}>{coach.readiness_level}</Badge>}
            </div>
            {sim.participant_name && (
              <p className="font-body text-sm text-muted-foreground mt-1.5"><span className="font-medium text-foreground">Participant:</span> {sim.participant_name}</p>
            )}
          </div>
          <div className="flex gap-2">
            <Button onClick={() => downloadReport('summary')} className="h-8 px-3 bg-transparent border border-border text-foreground hover:bg-muted gap-1.5 text-xs rounded-lg shadow-none"><Download className="w-3.5 h-3.5" /> PDF</Button>
            <Button data-testid="back-to-sim-btn" onClick={() => navigate(`/simulation/${simId}`)} className="h-8 px-3 bg-brand text-[#333333] gap-1.5 text-xs rounded-lg hover:opacity-90"><MessageCircle className="w-3.5 h-3.5" /> View Chat</Button>
          </div>
        </div>

        {/* Overall Score Card */}
        <div className="heritage-card p-6 mb-6" data-testid="overall-score-card">
          <div className="flex flex-col sm:flex-row sm:items-center gap-6">
            <div className="flex items-center gap-4">
              <ScoreCircle score={score.overall_score || 0} size={100} />
              <div>
                <p className="font-heading text-sm text-muted-foreground">Overall Score</p>
                <p className="font-heading text-3xl font-bold">{score.overall_score || 0}<span className="text-base text-muted-foreground">/10</span></p>
              </div>
            </div>
            <div className="flex-1">
              <p className="font-body text-sm text-foreground leading-relaxed">{coach.executive_summary || score.summary || ''}</p>
            </div>
          </div>
        </div>

        {/* Competency Scores Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
          {[
            { key: 'communication_clarity', label: 'Communication Clarity', icon: MessageCircle, color: 'text-sky-400' },
            { key: 'empathy_eq', label: 'Empathy & EQ', icon: Heart, color: 'text-pink-400' },
            { key: 'problem_solving', label: 'Problem Solving', icon: Brain, color: 'text-purple-400' },
            { key: 'professionalism', label: 'Professionalism', icon: Shield, color: 'text-emerald-400' },
            { key: 'outcome_effectiveness', label: 'Outcome Effectiveness', icon: Target, color: 'text-amber-400' },
            { key: 'tone_vocal_presence', label: 'Tone & Vocal Presence', icon: Activity, color: 'text-cyan-400' },
            { key: 'emotion_management', label: 'Emotion Management', icon: Sparkles, color: 'text-orange-400' },
          ].map(c => {
            const compData = comp[c.key] || {};
            const val = compData.score || score[c.key.replace('empathy_eq', 'empathy')] || 0;
            const weight = compData.weight || 0;
            const pct = (val / 10) * 100;
            const barColor = val >= 8 ? 'bg-emerald-400' : val >= 6 ? 'bg-amber-400' : 'bg-red-400';
            return (
              <div key={c.key} className="heritage-card p-4" data-testid={`competency-${c.key}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <c.icon className={`w-4 h-4 ${c.color}`} />
                    <span className="font-heading text-xs font-bold">{c.label}</span>
                    {weight > 0 && <span className="text-[10px] text-muted-foreground">({weight}%)</span>}
                  </div>
                  <span className="font-heading text-sm font-bold">{val}/10</span>
                </div>
                <div className="h-1.5 bg-muted rounded-full overflow-hidden mb-2">
                  <div className={`h-full ${barColor} rounded-full transition-all duration-700`} style={{ width: `${pct}%` }} />
                </div>
                {compData.sub_scores && (
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 mb-2">
                    {Object.entries(compData.sub_scores).map(([k, v]) => (
                      <span key={k} className="text-[10px] text-muted-foreground">
                        {k.replace(/_/g, ' ')}: <span className="font-bold text-foreground">{v}</span>
                      </span>
                    ))}
                  </div>
                )}
                {compData.evidence && <p className="font-body text-[11px] text-muted-foreground italic leading-relaxed">{compData.evidence}</p>}
              </div>
            );
          })}
        </div>

        {/* Tone Analysis */}
        {sec('tone_analysis') && tone.dominant_tone && (
          <div className="heritage-card p-5 mb-6" data-testid="tone-analysis">
            <h2 className="font-heading text-sm font-bold mb-3 flex items-center gap-2"><Activity className="w-4 h-4 text-cyan-400" /> Tone Analysis</h2>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <p className="font-body text-xs text-muted-foreground mb-2">Dominant Tone: <span className="font-bold text-foreground">{tone.dominant_tone}</span></p>
                {tone.distribution && (
                  <div className="space-y-1.5">
                    {Object.entries(tone.distribution).filter(([, v]) => v > 0).sort(([, a], [, b]) => b - a).map(([k, v]) => (
                      <div key={k} className="flex items-center gap-2">
                        <span className="font-body text-[11px] text-muted-foreground w-24 capitalize">{k}</span>
                        <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                          <div className="h-full bg-brand rounded-full transition-all" style={{ width: `${v}%` }} />
                        </div>
                        <span className="font-heading text-[11px] font-bold w-8 text-right">{v}%</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="sm:w-48 heritage-card p-3 bg-muted/30">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">Appropriateness</p>
                <p className="font-heading text-2xl font-bold">{tone.appropriateness_score}/10</p>
                {tone.insight && <p className="font-body text-[11px] text-muted-foreground mt-1">{tone.insight}</p>}
              </div>
            </div>
          </div>
        )}

        {/* Emotion Journey */}
        {sec('emotion_analysis') && journey.start && (
          <div className="heritage-card p-5 mb-6" data-testid="emotion-journey">
            <h2 className="font-heading text-sm font-bold mb-4 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-orange-400" /> Emotion Journey</h2>
            <div className="grid grid-cols-3 gap-3 mb-4">
              {[
                { phase: 'Start', data: journey.start, color: 'border-sky-400/30 bg-sky-400/5' },
                { phase: 'Middle', data: journey.middle, color: 'border-amber-400/30 bg-amber-400/5' },
                { phase: 'End', data: journey.end, color: 'border-emerald-400/30 bg-emerald-400/5' },
              ].map(p => (
                <div key={p.phase} className={`rounded-xl border p-3 ${p.color}`}>
                  <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground mb-1">{p.phase}</p>
                  <div className="flex flex-wrap gap-1 mb-1.5">
                    {(p.data?.emotions || []).map((e, i) => <Badge key={`${p.phase}-${e}-${i}`} className="bg-white/10 text-foreground border-none text-[10px] px-1.5 py-0 capitalize">{e}</Badge>)}
                  </div>
                  <p className="font-body text-[10px] text-muted-foreground leading-relaxed">{p.data?.description || ''}</p>
                </div>
              ))}
            </div>
            {/* Regulation Score */}
            <div className="flex items-center gap-4 bg-muted/30 rounded-xl p-3">
              <div>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Emotional Regulation</p>
                <p className="font-heading text-2xl font-bold">{journey.emotional_regulation_score || 0}<span className="text-sm text-muted-foreground">/100</span></p>
              </div>
              <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all duration-700 ${(journey.emotional_regulation_score || 0) >= 70 ? 'bg-emerald-400' : (journey.emotional_regulation_score || 0) >= 50 ? 'bg-amber-400' : 'bg-red-400'}`} style={{ width: `${journey.emotional_regulation_score || 0}%` }} />
              </div>
              <p className="font-body text-[11px] text-muted-foreground max-w-xs">{journey.regulation_insight || ''}</p>
            </div>
          </div>
        )}

        {/* AI Coach Summary */}
        {sec('ai_coach_summary') && coach.readiness_level && (
          <div className="heritage-card p-5 mb-6" data-testid="ai-coach-summary">
            <h2 className="font-heading text-sm font-bold mb-3 flex items-center gap-2"><GraduationCap className="w-4 h-4 text-purple-400" /> AI Coach Summary</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
              <div>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1.5 flex items-center gap-1"><CheckCircle className="w-3 h-3 text-emerald-400" /> Key Strengths</p>
                <ul className="space-y-1">{(coach.key_strengths || []).map((s, i) => <li key={`str-${i}-${s.slice(0,20)}`} className="font-body text-xs text-foreground flex items-start gap-1.5"><ChevronRight className="w-3 h-3 mt-0.5 text-emerald-400 flex-shrink-0" />{s}</li>)}</ul>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1.5 flex items-center gap-1"><XCircle className="w-3 h-3 text-amber-400" /> Development Priorities</p>
                <ul className="space-y-1">{(coach.development_priorities || []).map((s, i) => <li key={`dev-${i}-${s.slice(0,20)}`} className="font-body text-xs text-foreground flex items-start gap-1.5"><ChevronRight className="w-3 h-3 mt-0.5 text-amber-400 flex-shrink-0" />{s}</li>)}</ul>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1.5 flex items-center gap-1"><Lightbulb className="w-3 h-3 text-sky-400" /> Learning Path</p>
                <ul className="space-y-1">{(coach.learning_path || []).map((s, i) => <li key={`lp-${i}-${s.slice(0,20)}`} className="font-body text-xs text-foreground flex items-start gap-1.5"><span className="text-[10px] text-sky-400 font-bold flex-shrink-0">{i + 1}.</span>{s}</li>)}</ul>
              </div>
            </div>
          </div>
        )}

        {/* Strengths & Improvements */}
        {sec('coaching_recommendations') && (<>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
          <div className="heritage-card p-4" data-testid="strengths-card">
            <p className="font-heading text-sm font-bold mb-2 flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-emerald-400" /> Strengths</p>
            <ul className="space-y-1.5">{(score.strengths || []).map((s, i) => <li key={`s-${i}-${s.slice(0,20)}`} className="font-body text-xs text-muted-foreground flex items-start gap-1.5"><ChevronRight className="w-3 h-3 mt-0.5 text-emerald-400/50 flex-shrink-0" />{s}</li>)}</ul>
          </div>
          <div className="heritage-card p-4" data-testid="improvements-card">
            <p className="font-heading text-sm font-bold mb-2 flex items-center gap-1.5"><XCircle className="w-4 h-4 text-amber-400" /> Areas for Improvement</p>
            <ul className="space-y-1.5">{(score.improvements || []).map((s, i) => <li key={i} className="font-body text-xs text-muted-foreground flex items-start gap-1.5"><ChevronRight className="w-3 h-3 mt-0.5 text-amber-400/50 flex-shrink-0" />{s}</li>)}</ul>
          </div>
        </div>

        {/* Top Focus Areas */}
        {score.top_focus_areas?.length > 0 && (
          <div className="heritage-card p-5 mb-6 border-l-4 border-brand" data-testid="focus-areas">
            <h2 className="font-heading text-sm font-bold mb-3 flex items-center gap-2"><Target className="w-4 h-4 text-brand" /> Top Focus Areas</h2>
            <div className="space-y-3">
              {score.top_focus_areas.map((fa, i) => (
                <div key={`fa-${fa.area?.slice(0,20)}-${i}`} className="bg-brand/5 rounded-xl p-4 border border-brand/10">
                  <p className="font-heading text-xs font-bold text-brand mb-1">#{i + 1}: {fa.area}</p>
                  <p className="font-body text-[11px] text-muted-foreground mb-1.5">{fa.why}</p>
                  <div className="flex items-start gap-1.5">
                    <Sparkles className="w-3 h-3 text-amber-400 mt-0.5 flex-shrink-0" />
                    <p className="font-body text-[11px] text-foreground font-medium">Quick win: {fa.quick_win}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        </>)}

        {/* Frameworks */}
        {sec('frameworks') && score.frameworks?.length > 0 && (
          <div className="heritage-card p-5 mb-6" data-testid="frameworks-section">
            <h2 className="font-heading text-sm font-bold mb-3 flex items-center gap-2"><Sparkles className="w-4 h-4 text-amber-400" /> Recommended Frameworks</h2>
            <div className="space-y-3">
              {score.frameworks.map((fw, i) => (
                <div key={`fw-${fw.name}-${i}`} className="bg-muted/30 rounded-xl p-4 border border-border/50">
                  <div className="flex items-center gap-2 mb-2">
                    <p className="font-heading text-xs font-bold text-brand">{fw.name}</p>
                    <p className="font-body text-[10px] text-muted-foreground italic">{fw.description}</p>
                  </div>
                  {fw.conversation_moment && (
                    <div className="bg-surface rounded-lg p-2.5 border border-border/30 mb-2">
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">Conversation Moment</p>
                      <p className="font-body text-[11px] text-foreground italic">&ldquo;{fw.conversation_moment}&rdquo;</p>
                    </div>
                  )}
                  {fw.ideal_response && (
                    <div className="bg-emerald-500/5 rounded-lg p-2.5 border border-emerald-500/10 mb-2">
                      <p className="text-[10px] text-emerald-500 uppercase tracking-wide mb-1">Ideal Response</p>
                      <p className="font-body text-[11px] text-foreground">&ldquo;{fw.ideal_response}&rdquo;</p>
                    </div>
                  )}
                  {fw.real_world_impact && (
                    <p className="font-body text-[11px] text-muted-foreground mb-1.5"><span className="font-semibold text-foreground">Impact:</span> {fw.real_world_impact}</p>
                  )}
                  <p className="font-body text-[11px] text-foreground"><span className="font-semibold">How to apply:</span> {fw.application}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Benchmarks */}
        {sec('benchmarks') && benchmarks && (
          <div className="heritage-card p-5 mb-6" data-testid="benchmarks-section">
            <h2 className="font-heading text-sm font-bold mb-3 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-info" /> Benchmarks</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
              {benchmarks.benchmarks.platform && (
                <BenchmarkCard icon={Users} label="Platform Average" avg={benchmarks.benchmarks.platform.average} percentile={benchmarks.benchmarks.platform.percentile} userScore={benchmarks.user_score} sample={benchmarks.benchmarks.platform.sample_size} color="text-info" />
              )}
              {benchmarks.benchmarks.team && (
                <BenchmarkCard icon={Users} label="Team Average" avg={benchmarks.benchmarks.team.average} percentile={benchmarks.benchmarks.team.percentile} userScore={benchmarks.user_score} sample={benchmarks.benchmarks.team.sample_size} color="text-success" />
              )}
              {benchmarks.benchmarks.organization && (
                <BenchmarkCard icon={Building2} label="Org Average" avg={benchmarks.benchmarks.organization.average} percentile={benchmarks.benchmarks.organization.percentile} userScore={benchmarks.user_score} sample={benchmarks.benchmarks.organization.sample_size} color="text-brand" />
              )}
            </div>
            {/* Previous Attempts Trend */}
            {benchmarks.benchmarks.previous_attempts?.length > 1 && (
              <div>
                <p className="font-body text-xs text-muted-foreground mb-2">Your Progress on This Scenario</p>
                <div className="flex items-end gap-1.5 h-16">
                  {benchmarks.benchmarks.previous_attempts.map((a, i) => (
                    <div key={`attempt-${i}-${a.score}`} className="flex-1 flex flex-col items-center">
                      <div className={`w-full rounded-t ${i === benchmarks.benchmarks.previous_attempts.length - 1 ? 'bg-brand' : 'bg-muted-foreground/30'}`} style={{ height: `${(a.score / 10) * 100}%` }} />
                      <span className="text-[9px] text-muted-foreground mt-1">{a.score}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

function BenchmarkCard({ icon: Icon, label, avg, percentile, userScore, sample, color }) {
  return (
    <div className="bg-muted/30 rounded-xl p-3 border border-border/50">
      <div className="flex items-center gap-1.5 mb-2">
        <Icon className={`w-3.5 h-3.5 ${color}`} />
        <span className="font-body text-[10px] text-muted-foreground uppercase tracking-wide">{label}</span>
      </div>
      <div className="flex items-baseline gap-2">
        <span className="font-heading text-lg font-bold">{avg || '--'}</span>
        <span className="text-[10px] text-muted-foreground">/10 avg ({sample} sims)</span>
      </div>
      {percentile !== null && percentile !== undefined && (
        <p className={`font-body text-xs font-bold mt-1 ${percentile >= 75 ? 'text-emerald-400' : percentile >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
          {percentile >= 50 ? `Top ${100 - percentile}%` : `${percentile}th percentile`}
        </p>
      )}
    </div>
  );
}

function ScoreCircle({ score, size = 100 }) {
  const r = (size - 10) / 2, c = 2 * Math.PI * r, o = c - (score / 10) * c;
  const color = score >= 8 ? 'var(--success)' : score >= 6 ? 'var(--warning)' : 'var(--accent)';
  return (
    <svg width={size} height={size} className="transform -rotate-90 flex-shrink-0">
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="var(--muted)" strokeWidth="5" />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth="5" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={o} className="transition-all duration-1000" />
      <text x={size/2} y={size/2} textAnchor="middle" dominantBaseline="central" fill="var(--fg)" className="font-heading font-bold" style={{ fontSize: size*0.28, transform: 'rotate(90deg)', transformOrigin: 'center' }}>{score}</text>
    </svg>
  );
}
